import { h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import PromptService from '../../services/PromptService.js';
import promptVersionControl from '../../services/PromptVersionControl.js';
import { Toast } from '../../utils/ui.js';
import { confirmOverlay } from '../../utils/Overlay.js';
import { ModalService } from '../../ui-kit/ModalService.jsx';

/**
 * PromptEditorIsland
 * Preact island version of the PromptEditor UI, mounted inside ModalService.
 * External API remains the same via the legacy PromptEditor wrapper (open method).
 */
export function PromptEditorIsland({ promptKey, promptName }) {
  // Load the current prompt snapshot once
  const initial = useMemo(() => {
    try { return PromptService.getPrompt(promptKey) || {}; } catch { return {}; }
  }, [promptKey]);

  const [text, setText] = useState(
    initial.systemRole || initial.template || initial.content || ''
  );
  const [temperature, setTemperature] = useState(
    typeof initial.temperature === 'number' ? initial.temperature : 0.7
  );
  const [changeType, setChangeType] = useState('patch');
  const [changeDescription, setChangeDescription] = useState('Updated prompt');

  // Version data
  const [stats, setStats] = useState(() => promptVersionControl.getStatistics(promptKey));
  const [history, setHistory] = useState(() => promptVersionControl.getHistory(promptKey, 5));
  const [currentVersion, setCurrentVersion] = useState(() => promptVersionControl.getCurrentVersion(promptKey));

  const variables = initial?.variables || null;

  const hasChanges = useMemo(() => {
    const original = (initial.systemRole || initial.template || initial.content || '').trim();
    return (text || '').trim() !== original;
  }, [text, initial]);

  // Update derived version data after operations
  const refreshVersionData = () => {
    setStats(promptVersionControl.getStatistics(promptKey));
    setHistory(promptVersionControl.getHistory(promptKey, 5));
    setCurrentVersion(promptVersionControl.getCurrentVersion(promptKey));
  };

  // Actions
  const onCancel = () => {
    if (hasChanges) {
      confirmOverlay('Discard Changes?', 'You have unsaved changes. Discard them?', () => {
        try { ModalService.closeTop(); } catch (_) {}
      }, () => {});
      return;
    }
    try { ModalService.closeTop(); } catch (_) {}
  };

  const onReset = () => {
    confirmOverlay('Reset to Default?', 'Restore the original prompt and discard customizations?', () => {
      try {
        const def = PromptService.getDefaultPrompt(promptKey) || {};
        setText(def.systemRole || def.template || '');
        setTemperature(typeof def.temperature === 'number' ? def.temperature : 0.7);
        Toast.info('Reset to default prompt');
      } catch (_) {}
    }, () => {});
  };

  const onSave = async () => {
    const newText = (text || '').trim();
    if (!newText) {
      Toast.error('Prompt cannot be empty');
      return;
    }
    try {
      // Save a version entry
      const newVersion = promptVersionControl.saveVersion(promptKey, newText, {
        changeType,
        description: changeDescription || 'Updated prompt',
        author: 'User'
      });

      // Apply to PromptService (persists to storage + emits events)
      PromptService.updatePrompt(promptKey, {
        systemRole: newText,
        temperature: typeof temperature === 'number' ? temperature : parseFloat(temperature) || 0.7,
        author: 'User',
        changeNote: changeDescription || 'Updated prompt'
      });

      refreshVersionData();
      Toast.success(`Prompt saved as version ${newVersion}`);
      try { ModalService.closeTop(); } catch (_) {}
    } catch (e) {
      console.error('Error saving prompt:', e);
      Toast.error('Failed to save prompt');
    }
  };

  const onRollback = (version) => {
    confirmOverlay(`Rollback to Version ${version}?`, `This restores the prompt to version ${version}. Continue?`, () => {
      try {
        const content = promptVersionControl.rollback(promptKey, version);
        setText(content || '');
        refreshVersionData();
        Toast.success(`Rolled back to version ${version}`);
      } catch (error) {
        Toast.error('Failed to rollback: ' + (error?.message || 'Unknown error'));
      }
    }, () => {});
  };

  // Render helpers
  const charColor = useMemo(() => {
    const len = (text || '').length;
    if (len > 4000) return 'var(--error-color)';
    if (len > 3000) return 'var(--warning-color)';
    return 'var(--text-secondary)';
  }, [text]);

  return (
    <div class="prompt-editor">
      {/* Version Info Bar */}
      <div class="version-info-bar">
        <div class="version-badge">
          <span class="version-label">Version:</span>
          <span class="version-number">{currentVersion}</span>
        </div>
        <div class="version-stats">
          <span title="Total versions">📚 {stats.totalVersions} versions</span>
          <span title="Major/Minor/Patch">🔄 {stats.majorChanges}/{stats.minorChanges}/{stats.patches}</span>
          <span title="Last modified">🕰️ {stats.lastModified ? new Date(stats.lastModified).toLocaleDateString() : 'Never'}</span>
        </div>
      </div>

      {/* Header Info */}
      <div class="editor-info">
        <div class="info-item">
          <span class="info-label">Prompt ID:</span>
          <span class="info-value">{promptKey}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Last Author:</span>
          <span class="info-value">{stats.lastAuthor || 'System'}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Change Frequency:</span>
          <span class="info-value">{stats.changeFrequency}</span>
        </div>
      </div>

      {/* Main Editor */}
      <div class="editor-section">
        <label for="prompt-editor-textarea">System Prompt:</label>
        <textarea
          id="prompt-editor-textarea"
          class="prompt-textarea"
          rows={15}
          placeholder="Enter the system prompt..."
          value={text}
          onInput={(e) => setText(e.currentTarget.value)}
        />
        <div class="char-count">
          <span id="char-count" style={{ color: charColor }}>{(text || '').length}</span> characters
        </div>
      </div>

      {/* Variables section */}
      {Array.isArray(variables) && variables.length > 0 ? (
        <div class="variables-section">
          <label>Available Variables:</label>
          <div class="variables-list">
            {variables.map((v) => (
              <span class="variable-tag">{`{${v}}`}</span>
            ))}
          </div>
          <p class="help-text">These variables will be replaced with actual values when the prompt is used.</p>
        </div>
      ) : null}

      {/* Temperature */}
      <div class="temperature-section">
        <label for="temperature-slider">
          Temperature: <span id="temperature-value">{typeof temperature === 'number' ? temperature : parseFloat(temperature) || 0.7}</span>
        </label>
        <input
          type="range"
          id="temperature-slider"
          min="0"
          max="1"
          step="0.1"
          class="temperature-slider"
          value={typeof temperature === 'number' ? temperature : parseFloat(temperature) || 0.7}
          onInput={(e) => setTemperature(parseFloat(e.currentTarget.value))}
        />
        <p class="help-text">Lower = More focused/deterministic | Higher = More creative/random</p>
      </div>

      {/* Version History */}
      {Array.isArray(history) && history.length > 0 ? (
        <div class="history-section">
          <label>Version History:</label>
          <div class="version-history-list">
            {history.map((v) => (
              <div class="version-history-item">
                <div class="version-item-header">
                  <span class="version-tag">v{v.version}</span>
                  <span class="version-date">{new Date(v.timestamp).toLocaleString()}</span>
                </div>
                <div class="version-item-details">
                  <span class="version-author">👤 {v.author}</span>
                  <span class="version-change">{v.changeDescription}</span>
                  {v.diff ? (
                    <span class="version-diff">+{v.diff.added} -{v.diff.removed} ±{v.diff.modified}</span>
                  ) : null}
                </div>
                <button class="btn-small" onClick={() => onRollback(v.version)}>Restore</button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Change Type + Description */}
      <div class="change-type-section">
        <label>Change Type for This Edit:</label>
        <div class="change-type-selector">
          <input type="radio" id="change-patch" name="changeType" value="patch" checked={changeType === 'patch'} onChange={() => setChangeType('patch')} />
          <label for="change-patch"><strong>Patch</strong> - Minor fixes, typos, small adjustments</label>

          <input type="radio" id="change-minor" name="changeType" value="minor" checked={changeType === 'minor'} onChange={() => setChangeType('minor')} />
          <label for="change-minor"><strong>Minor</strong> - New features, moderate changes</label>

          <input type="radio" id="change-major" name="changeType" value="major" checked={changeType === 'major'} onChange={() => setChangeType('major')} />
          <label for="change-major"><strong>Major</strong> - Breaking changes, complete rewrites</label>
        </div>

        <div class="change-description">
          <label for="change-description">Change Description:</label>
          <input type="text" id="change-description" placeholder="Brief description of changes..." value={changeDescription}
                 onInput={(e) => setChangeDescription(e.currentTarget.value)} />
        </div>
      </div>

      {/* Tips */}
      <div class="tips-section">
        <h4>💡 Tips for Effective Prompts:</h4>
        <ul>
          <li>Be specific about the desired output format</li>
          <li>Include examples of good responses</li>
          <li>Specify what to avoid or not include</li>
          <li>Keep instructions clear and concise</li>
          <li>Test with different temperature values</li>
        </ul>
      </div>

      {/* Local footer actions for better control */}
      <div class="uk-modal-footer">
        <button class="uk-btn uk-btn-secondary" onClick={onCancel}>Cancel</button>
        <button class="uk-btn uk-btn-secondary" onClick={onReset}>Reset to Default</button>
        <button class="uk-btn uk-btn-primary" onClick={onSave}>Save & Apply</button>
      </div>
    </div>
  );
}

export default PromptEditorIsland;

